import React, { createContext, useContext, useEffect, useState } from 'react';
import { getEnvConfigSelector } from '../helpers/env';
import {
  SelectedConfig,
  getSelectedConfig as getStoredConfig,
  setSelectedConfig as setStoredConfig,
  removeSelectedConfig,
} from '../helpers/storage';
import { useAppDispatch } from '../hooks/app';
import { logout } from '../store/session/slices';

interface ConfigContextState {
  enabled: boolean;
  configs: SelectedConfig[];
  selectedConfig: SelectedConfig | null;
  selectConfig: (config: SelectedConfig) => void;
}

const ConfigContext = createContext<ConfigContextState>({
  enabled: false,
  configs: [],
  selectedConfig: null,
  selectConfig: () => {},
});

export const useConfig = () => useContext(ConfigContext);

export const ConfigProvider = ({ children }: { children: React.ReactNode }) => {
  const enabled = getEnvConfigSelector();
  const dispatch = useAppDispatch();
  const [configs, setConfigs] = useState<SelectedConfig[]>([]);
  const [selectedConfig, setSelectedConfig] = useState<SelectedConfig | null>(
    () => getStoredConfig()
  );

  useEffect(() => {
    if (!enabled) return;
    fetch('/configs.json')
      .then((res) => res.json())
      .then((data: SelectedConfig[]) => {
        setConfigs(data);
        const stored = getStoredConfig();
        if (!stored?.name) return;
        const fresh = data.find((c) => c.name === stored.name);
        if (fresh) {
          setStoredConfig(fresh);
          setSelectedConfig(fresh);
        } else {
          removeSelectedConfig();
          setSelectedConfig(null);
          dispatch(logout());
        }
      })
      .catch(() => setConfigs([]));
  }, [enabled, dispatch]);

  const selectConfig = (config: SelectedConfig) => {
    setStoredConfig(config);
    setSelectedConfig(config);
  };

  return (
    <ConfigContext.Provider value={{ enabled, configs, selectedConfig, selectConfig }}>
      {children}
    </ConfigContext.Provider>
  );
};
